import React from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { Switch } from '@rsos/components/capstone/base';
import { selectCapstoneAudioSettingsForFeature } from '@rsos/sinatra/src/selectors/selectAudioSettings';
import useIsIRP from '@rsos/utils/useIsIRP';
import { AdminDispatch } from '../../../../app/store';
import { usePatchAudioSettings } from '../../app/patchAudioSettings';
import SoundSelect from '../SoundSelect';
import { trackToggleSoundOnOff } from '../audioSettingsTrackings';
import styles from '../settings.module.css';

export const FollowupMessageAudioSetttings = () => {
  const dispatch = useDispatch<AdminDispatch>();
  const { follow_up: followUpSettings } = useSelector(
    selectCapstoneAudioSettingsForFeature('sms'),
  );

  const {
    active_participants: activeParticipantSettings,
    everyone_else: everyoneElseSettings,
  } = followUpSettings;

  const patchAudioSettings = usePatchAudioSettings('sms');

  // This is Very Typescripty; but all it really means is that you can make a
  // settings-updater for either role: "active_participants" or "everyone_else".
  // Saying it with keyof types just says that in a way that lets us add
  // additional roles in the list beyond those two.
  const updateSettings = <T extends keyof typeof followUpSettings>(role: T) => {
    return (update: Partial<(typeof followUpSettings)[T]>) => {
      dispatch(
        patchAudioSettings({
          follow_up: { [role]: update },
        }),
      );
    };
  };

  const { irpVersion } = useIsIRP();
  const toggleSoundOn = <T extends keyof typeof followUpSettings>(role: T) => {
    const updater = updateSettings<T>(role);
    const value = followUpSettings[role].sound_on;
    return () => {
      updater({ sound_on: !value } as Partial<(typeof followUpSettings)[T]>);
      trackToggleSoundOnOff(irpVersion, value, 'sms-follow-up-' + role);
    };
  };

  return (
    <div className={styles.card}>
      <div>
        <div className={styles.cardTitle}>Follow-up Reply</div>
        <div className={styles.cardDescription}>
          Notify your agents with sound when they receive a follow-up reply for
          any active text session.
        </div>
      </div>
      <div>
        <div className={styles.inputLabel}>Active Participants</div>
        <div className={styles.inputDescription}>
          Configure notifications for active participants.
        </div>
      </div>
      <div className={styles.soundBlock}>
        <span>Sound Notification</span>
        <Switch
          onOffLabel
          onClick={toggleSoundOn('active_participants')}
          isOn={activeParticipantSettings.sound_on}
        />
      </div>
      <SoundSelect
        isSoundOn={activeParticipantSettings.sound_on}
        type={'sms'}
        tone={activeParticipantSettings.tone}
        updateAudioSettings={updateSettings('active_participants')}
      />
      <hr />
      <div>
        <div className={styles.inputLabel}>All Other Users</div>
        <div className={styles.inputDescription}>
          Configure notifications for all other users.
        </div>
      </div>
      <div className={styles.soundBlock}>
        <span>Sound Notification</span>
        <Switch
          onOffLabel
          onClick={toggleSoundOn('everyone_else')}
          isOn={everyoneElseSettings.sound_on}
        />
      </div>
      <SoundSelect
        isSoundOn={everyoneElseSettings.sound_on}
        type={'sms'}
        tone={everyoneElseSettings.tone}
        updateAudioSettings={updateSettings('everyone_else')}
      />
    </div>
  );
};
